import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { resolveUploadUrl } from '../../utils/apiClient';
import { getTimesheetEmployees, getTimesheetClientCompanies, createTimesheet, fetchTimesheets, updateTimesheet, deleteTimesheet, exportTimesheets, updateTimesheetEmployeeVisibility } from '../../services/timesheetService';
import { getActivityTypes } from '../../services/activityTypeService';
import { useCurrentUser } from '../../context/UserContext';
import { usePermissions } from '../../hooks/usePermission';
import FormModal from "../../components/forms/FormModal";
import Toast from '../../components/Toast';
import ConfirmDialog from '../../components/ConfirmDialog';
import DurationScrubber from '../../components/forms/DurationScrubber';
import CustomDropdown from '../../components/forms/CustomDropdown';
import { getBookings } from "../../services/scheduleService";
import { getProjectsForEmployee } from '../../services/peopleService';
import { getAssignedMilestones } from '../../services/projectService';
import { createTimeOffRequest, getTimeOffTypes, getTimeOffRequests, updateTimeOffRequest, cancelTimeOffRequest } from '../../services/timeOffService';
import ApprovalList from '../../components/timeoff/ApprovalList';
import { usePendingApprovals } from '../../context/PendingApprovalsContext';
import { buildTimeOffCreatePayload, buildTimeOffUpdatePayload } from '../../utils/timeOffForm';
import { countLeaveDays, formatDays } from '../../utils/leaveDays';
import useNotificationCounts from '../../hooks/useNotificationCounts';
import styles from './Schedule.module.css';
import ui from './DashboardUI.module.css';
import useMinimumLoader from '../../hooks/useMinimumLoader';

const getMonday = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

const getDates = (startDate, numDays) => {
  const dates = [];
  for (let i = 0; i < numDays; i++) {
    const d = new Date(startDate);
    d.setDate(startDate.getDate() + i);
    dates.push(d);
  }
  return dates;
};

const DAY_OPTIONS = [
  { value: 7, label: '7 Days' },
  { value: 14, label: '14 Days' },
  { value: 28, label: '28 Days' },
];

// Helper to get initials from a name
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const Timesheets = () => {
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const { hasPermission } = usePermissions();
  const { refreshPendingCount } = usePendingApprovals();

  // Permission checks
  const canViewAllTimesheets = hasPermission('view_timesheets');
  const canViewOwnTimesheets = hasPermission('view_own_timesheets');
  const canCreateTimesheets = hasPermission('create_timesheets');
  const canEditTimesheets = hasPermission('edit_timesheets');
  const canDeleteTimesheets = hasPermission('delete_timesheets');
  const canViewTimeOff = hasPermission('timeoff:view:self') || hasPermission('timeoff:view:all') || hasPermission('timeoff:approve');
  const canEditOwnPendingTimeOff = hasPermission('timeoff:edit:self:pending');
  const canEditTeamPendingTimeOff = hasPermission('timeoff:edit:team:pending');
  const canEditApprovedTimeOff = hasPermission('timeoff:edit:approved');
  const canApproveTimeOff = hasPermission('timeoff:approve');
  const canViewAllTimeOff = hasPermission('timeoff:view:all');
  const hasAnyTimeOffPermission = canViewTimeOff || canEditOwnPendingTimeOff || canEditTeamPendingTimeOff || canEditApprovedTimeOff || canApproveTimeOff;

  const [employees, setEmployees] = useState([]);
  const [currentWeekStart, setCurrentWeekStart] = useState(getMonday(new Date()));
  const [daysToShow, setDaysToShow] = useState(14);
  const [showTimesheetModal, setShowTimesheetModal] = useState(false);
  const [activeTab, setActiveTab] = useState("timesheet");
  const [timesheets, setTimesheets] = useState([]);
  const [scheduledBookings, setScheduledBookings] = useState([]);
  const [employeeProjects, setEmployeeProjects] = useState([]);
  const [employeeMilestones, setEmployeeMilestones] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [toast, setToast] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showVisibilityModal, setShowVisibilityModal] = useState(false);
  const [visibilityEmployees, setVisibilityEmployees] = useState([]);
  const [isVisibilityLoading, setIsVisibilityLoading] = useState(false);
  const [prefillBooking, setPrefillBooking] = useState(null);
  const [timeOffRequests, setTimeOffRequests] = useState([]);
  const [timeOffTypes, setTimeOffTypes] = useState([]);
  const [showCancelTimeOffConfirm, setShowCancelTimeOffConfirm] = useState(false);
  const [timeOffToCancel, setTimeOffToCancel] = useState(null);
  const [showTimeOffApprovalModal, setShowTimeOffApprovalModal] = useState(false);
  const [selectedTimeOffForApproval, setSelectedTimeOffForApproval] = useState(null);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [pendingTimeOffRequests, setPendingTimeOffRequests] = useState([]);
  const notificationCounts = useNotificationCounts();
  const pendingNotificationsCount = notificationCounts.timesheets || 0;
  const [exportClientCompanies, setExportClientCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const showLoading = useMinimumLoader(loading);
  const [convertedBookings, setConvertedBookings] = useState(() => {
    // Load converted bookings from localStorage
    try {
      const stored = localStorage.getItem('convertedBookings');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const visibleDates = getDates(currentWeekStart, daysToShow);

  const [timesheetForm, setTimesheetForm] = useState({
    timesheetId: null,
    employeeId: "",
    workDate: "",
    projectId: "",
    projectName: "",
    projectCompanyName: "",
    milestoneId: "",
    activityTypeId: "",
    startTime: "09:00",
    endTime: "17:00",
    description: "",
  });
  const [companyFilter, setCompanyFilter] = useState("");

  const [timeOffForm, setTimeOffForm] = useState({
    timeOffId: null,
    timeOffTypeId: "",
    startDate: "",
    endDate: "",
    reason: "",
    employee_id: '',
    startHalfDay: false,
    endHalfDay: false,
  });

  const [exportForm, setExportForm] = useState({
    startDate: getMonday(new Date()).toISOString().split('T')[0],
    endDate: new Date(getMonday(new Date()).getTime() + (14 - 1) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    employeeId: "",
    projectId: "",
    clientCompanyId: "",
    includeMilestones: true,
    includeActivityTypes: true,
    groupBy: "", // empty = no grouping, "company" = client company, "contact" = client name
  });

  const exportClientCompanyOptions = (exportClientCompanies || []).map((company) => ({
    value: String(company.client_company_id),
    label: company.company_name,
  }));

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const toLocalISODate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const normalizeDateString = (dateString) => {
    if (dateString.includes('T')) {
      return dateString.split('T')[0];
    }
    return dateString;
  };

  const fetchTimesheetsData = useCallback(async () => {
    try {
      const start = toLocalISODate(currentWeekStart);
      const endDate = new Date(currentWeekStart);
      endDate.setDate(endDate.getDate() + daysToShow - 1);
      const end = toLocalISODate(endDate);
      const res = await fetchTimesheets({ start_date: start, end_date: end });
    
      setTimesheets(res || []);
    } catch (err) {
      console.error("Failed to fetch timesheets:", err);
      showToast(err.message || "Failed to fetch timesheets", "error");
      setTimesheets([]);
    }
  }, [currentWeekStart, daysToShow]);

  const fetchScheduledBookings = useCallback(async () => {
    try {
      const start = toLocalISODate(currentWeekStart);
      const endDate = new Date(currentWeekStart);
      endDate.setDate(endDate.getDate() + daysToShow - 1);
      const end = toLocalISODate(endDate);
      const res = await getBookings(start, end);
      
      // Process bookings to split multi-day bookings into daily bookings
      const processedBookings = [];
      res.forEach(booking => {
        const startDate = new Date(booking.start_datetime);
        const endDate = new Date(booking.end_datetime);
        const totalHours = booking.hours_allocated;
        const days = [];
        for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
          days.push(new Date(d));
        }
        const numDays = days.length;
        const hoursPerDay = totalHours / numDays;
        days.forEach(day => {
          const dateKey = day.toISOString().split('T')[0];
          processedBookings.push({
            ...booking,
            bookingDayKey: `${booking.id}-${dateKey}`, // Unique key for each booking-day combination
            start_datetime: dateKey + 'T' + booking.start_datetime.split('T')[1],
            end_datetime: dateKey + 'T' + booking.end_datetime.split('T')[1],
            hours_allocated: hoursPerDay,
          });
        });
      });
      
      setScheduledBookings(processedBookings.filter(booking => !convertedBookings.includes(booking.bookingDayKey)));
    } catch (err) {
      console.error("Failed to fetch scheduled bookings:", err);
      showToast(err.message || "Failed to fetch scheduled bookings", "error");
    }
  }, [currentWeekStart, daysToShow, convertedBookings]);

  const fetchTimeOffRequestsData = useCallback(async () => {
    if (!hasAnyTimeOffPermission) {
      setTimeOffRequests([]);
      return;
    }

    try {
      const start = toLocalISODate(currentWeekStart);
      const endDate = new Date(currentWeekStart);
      endDate.setDate(endDate.getDate() + daysToShow - 1);
      const end = toLocalISODate(endDate);
      const res = await getTimeOffRequests({ 
        status: 'approved,pending',
        start_date: start, 
        end_date: end 
      });
      
      setTimeOffRequests(res || []);
    } catch (err) {
      console.error("Failed to fetch time off requests:", err);
      setTimeOffRequests([]);
    }
  }, [currentWeekStart, daysToShow, hasAnyTimeOffPermission]);

  const fetchTimesheetEmployeesData = useCallback(async () => {
    if (!canViewAllTimesheets && !canViewOwnTimesheets) return;

    try {
      setLoading(true);
      const employeeData = await getTimesheetEmployees();
      const mapped = employeeData.map((u) => ({
        id: u.user_id,
        employee_id: u.employee_id,
        name: u.name,
        avatar_url: u.avatar_url,
        job_title: u.job_title
      }));
      setEmployees(mapped);
    } catch (error) {
      console.error("Error fetching employees:", error);
      showToast(error.message || "Failed to fetch employees", "error");
    } finally {
      setLoading(false);
    }
  }, [canViewAllTimesheets, canViewOwnTimesheets]);

  const fetchTimesheetVisibilityEmployees = useCallback(async () => {
    try {
      setIsVisibilityLoading(true);
      const employeeData = await getTimesheetEmployees({ include_hidden: true });
      const mapped = employeeData.map((employee) => ({
        employee_id: employee.employee_id,
        name: employee.name,
        job_title: employee.job_title,
        hide_on_timesheets: !!employee.hide_on_timesheets,
      }));
      setVisibilityEmployees(mapped);
    } catch (error) {
      console.error("Error fetching timesheet visibility employees:", error);
      showToast(error.message || "Failed to fetch employee visibility", "error");
    } finally {
      setIsVisibilityLoading(false);
    }
  }, []);

  const handleTimesheetVisibilityToggle = async (employeeId, hidden) => {
    try {
      await updateTimesheetEmployeeVisibility(employeeId, hidden);
      await Promise.all([fetchTimesheetEmployeesData(), fetchTimesheetVisibilityEmployees()]);
      showToast(`Employee ${hidden ? 'hidden from' : 'shown on'} timesheets`, 'success');
    } catch (error) {
      console.error("Error updating timesheet visibility:", error);
      showToast(error.message || "Failed to update timesheet visibility", "error");
    }
  };

  useEffect(() => {
    fetchTimesheetEmployeesData();
  }, [fetchTimesheetEmployeesData]);

  useEffect(() => {
    const fetchActivityTypes = async () => {
      try {
        const response = await getActivityTypes();
        setActivityTypes(response.activityTypes || []);
      } catch (err) {
        console.error("Failed to fetch activity types:", err);
      }
    };

    fetchActivityTypes();
  }, []);

  useEffect(() => {
    if (!hasAnyTimeOffPermission) return;

    const fetchTimeOffTypesData = async () => {
      try {
        const types = await getTimeOffTypes();
        setTimeOffTypes(types || []);

        if (types?.length > 0) {
          const firstTypeId = String(types[0].leave_type_id || types[0].id || '');
          setTimeOffForm((prev) => ({ ...prev, timeOffTypeId: firstTypeId }));
        }
      } catch (err) {
        console.error("Failed to fetch time off types:", err);
        showToast(err.message || "Failed to fetch time off types", "error");
      }
    };

    fetchTimeOffTypesData();
  }, [hasAnyTimeOffPermission]);

  useEffect(() => {
    if (canViewAllTimesheets || canViewOwnTimesheets) {
      fetchTimesheetsData();
      fetchScheduledBookings();
      fetchTimeOffRequestsData();
    }
  }, [fetchTimesheetsData, fetchScheduledBookings, fetchTimeOffRequestsData, canViewAllTimesheets, canViewOwnTimesheets]);

  // Update export form dates when view changes
  useEffect(() => {
    const endDate = new Date(currentWeekStart);
    endDate.setDate(currentWeekStart.getDate() + daysToShow - 1);
    setExportForm(prev => ({
      ...prev,
      startDate: currentWeekStart.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    }));
  }, [currentWeekStart, daysToShow]);

  useEffect(() => {
    const fetchProjectsAndMilestones = async () => {
      if (!timesheetForm.employeeId) {
        setEmployeeProjects([]);
        setEmployeeMilestones([]);
        return;
      }

      try {
        const projectData = await getProjectsForEmployee(timesheetForm.employeeId);
        setEmployeeProjects(projectData || []);

        // Fetch milestones if a project is selected
        if (timesheetForm.projectId) {
          const milestones = await getAssignedMilestones(timesheetForm.projectId, timesheetForm.employeeId);
          setEmployeeMilestones(milestones || []);
        } else {
          setEmployeeMilestones([]);
        }
      } catch (err) {
        console.error("Could not fetch employee's projects or milestones", err);
        showToast(err.message || "Failed to fetch employee projects", "error");
        setEmployeeProjects([]);
        setEmployeeMilestones([]);
      }
    };

    fetchProjectsAndMilestones();
  }, [timesheetForm.employeeId, timesheetForm.projectId]);

  // Sync companyFilter when modal opens or selected project changes externally (edit/prefill)
  useEffect(() => {
    if (!showTimesheetModal) {
      setCompanyFilter("");
      return;
    }
    if (timesheetForm.projectId) {
      setCompanyFilter(timesheetForm.projectCompanyName || "__none__");
    }
  }, [showTimesheetModal, timesheetForm.projectId, timesheetForm.projectCompanyName]);

  // Unique company options derived from employee's assigned projects
  const timesheetCompanyOptions = useMemo(() => {
    const names = new Set();
    let hasNone = false;
    (employeeProjects || []).forEach((p) => {
      if (p.company_name) names.add(p.company_name);
      else hasNone = true;
    });
    const opts = [{ value: "", label: "All companies" }];
    Array.from(names).sort().forEach((name) => opts.push({ value: name, label: name }));
    if (hasNone) opts.push({ value: "__none__", label: "No company" });
    return opts;
  }, [employeeProjects]);

  const filteredEmployeeProjects = useMemo(() => {
    if (!companyFilter) return employeeProjects || [];
    if (companyFilter === "__none__") {
      return (employeeProjects || []).filter((p) => !p.company_name);
    }
    return (employeeProjects || []).filter((p) => p.company_name === companyFilter);
  }, [employeeProjects, companyFilter]);

  useEffect(() => {
    const fetchExportClientCompanies = async () => {
      if (!showExportModal) return;
      try {
        const companies = await getTimesheetClientCompanies();
        setExportClientCompanies(companies || []);
      } catch (err) {
        console.error("Failed to fetch timesheet client companies:", err);
        setExportClientCompanies([]);
      }
    };

    fetchExportClientCompanies();
  }, [showExportModal]);

  const handleTimesheetSubmit = async (e) => {
    e.preventDefault();

    if (!timesheetForm.projectId && !timesheetForm.activityTypeId) {
      showToast('Please select either a project or an activity type', 'error');
      return;
    }

    if ((timesheetForm.endTime || '') <= (timesheetForm.startTime || '')) {
      showToast('Please set a duration greater than 0', 'error');
      return;
    }

    // Permission check
    const isEditingOwnTimesheet = timesheetForm.employeeId.toString() === currentUser.user_id.toString();
    if (!isEditingOwnTimesheet && !canEditTimesheets) {
      showToast('You do not have permission to edit timesheets for other employees', 'error');
      return;
    }

    if (isEditingOwnTimesheet && !canCreateTimesheets && !canEditTimesheets) {
      showToast('You do not have permission to create or edit timesheets', 'error');
      return;
    }

    try {
      const payload = {
        employee_id: parseInt(timesheetForm.employeeId),
        project_id: timesheetForm.projectId ? parseInt(timesheetForm.projectId) : null,
        milestone_id: timesheetForm.milestoneId ? parseInt(timesheetForm.milestoneId) : null,
        activity_type_id: timesheetForm.activityTypeId ? parseInt(timesheetForm.activityTypeId) : null,
        work_date: timesheetForm.workDate,
        start_time: timesheetForm.startTime,
        end_time: timesheetForm.endTime,
        description: timesheetForm.description || '',
      };

      if (timesheetForm.timesheetId) {
        await updateTimesheet(timesheetForm.timesheetId, payload);
        showToast('Timesheet updated successfully', 'success');
      } else {
        await createTimesheet(payload);
        showToast('Timesheet created successfully', 'success');
      }

      if (prefillBooking) {
        setConvertedBookings(prev => {
          const updated = [...prev, prefillBooking.bookingDayKey];
          localStorage.setItem('convertedBookings', JSON.stringify(updated));
          return updated;
        });
        setScheduledBookings(prev => prev.filter(b => b.bookingDayKey !== prefillBooking.bookingDayKey));
        setPrefillBooking(null);
      }

      await fetchTimesheetsData();
      setShowTimesheetModal(false);
      setTimesheetForm({
        timesheetId: null,
        employeeId: "",
        workDate: "",
        projectId: "",
        projectName: "",
        projectCompanyName: "",
        milestoneId: "",
        activityTypeId: "",
        startTime: "09:00",
        endTime: "17:00",
        description: "",
      });
    } catch (error) {
      console.error("Timesheet save failed:", error);
      showToast(error.message || "Failed to save timesheet", "error");
    }
  };

  const handleExportSubmit = async (e) => {
    e.preventDefault();

    try {
      // Build query params for export
      const params = {
        start_date: exportForm.startDate,
        end_date: exportForm.endDate,
      };

      if (exportForm.employeeId) params.employee_id = exportForm.employeeId;
      if (exportForm.projectId) params.project_id = exportForm.projectId;
      if (exportForm.clientCompanyId) params.client_company_id = exportForm.clientCompanyId;
      if (exportForm.includeMilestones) params.include_milestones = 'true';
      if (exportForm.includeActivityTypes) params.include_activity_types = 'true';
      if (exportForm.groupBy) params.group_by = exportForm.groupBy;

      // Call the export service (to be implemented)
      await exportTimesheets(params);
      showToast('Timesheets exported successfully', 'success');
      setShowExportModal(false);
    } catch (error) {
      console.error("Export failed:", error);
      showToast(error.message || "Failed to export timesheets", "error");
    }
  };

  const handleTimeOffSubmit = async (e) => {
    e.preventDefault();

    if (!timeOffForm.timeOffTypeId || !timeOffForm.startDate || !timeOffForm.endDate) {
      showToast('Please fill in all required fields', 'error');
      return;
    }

    try {
      if (timeOffForm.timeOffId) {
        await updateTimeOffRequest(timeOffForm.timeOffId, buildTimeOffUpdatePayload(timeOffForm));
        showToast('Time off request updated successfully', 'success');
      } else {
        await createTimeOffRequest(buildTimeOffCreatePayload(timeOffForm, currentUser.user_id));
        showToast('Time off request submitted successfully', 'success');
      }

      await fetchTimeOffRequestsData();
      await fetchPendingTimeOffRequests();
      refreshPendingCount();
      setShowTimesheetModal(false);
      setActiveTab("timesheet");
      setTimeOffForm({
        employee_id: '',
        timeOffTypeId: timeOffTypes.length > 0 ? String(timeOffTypes[0].leave_type_id || timeOffTypes[0].id || '') : "",
        startDate: "",
        endDate: "",
        reason: "",
      });
    } catch (error) {
      console.error("Time off request failed:", error);
      showToast(error.response?.data?.error || error.message || "Failed to submit time off request", "error");
    }
  };

  const handleApproveTimeOff = async (timeOffId) => {
    try {
      await updateTimeOffRequest(timeOffId, { status: 'approved' });
      showToast('Time off request approved', 'success');
      await fetchTimeOffRequestsData();
      await fetchPendingTimeOffRequests();
      refreshPendingCount();
      setShowTimeOffApprovalModal(false);
      setShowNotificationsModal(false);
    } catch (error) {
      showToast('Failed to approve time off', 'error');
    }
  };

  const handleDenyTimeOff = async (timeOffId) => {
    try {
      await updateTimeOffRequest(timeOffId, { status: 'rejected' });
      showToast('Time off request rejected', 'success');
      await fetchTimeOffRequestsData();
      await fetchPendingTimeOffRequests();
      refreshPendingCount();
      setShowTimeOffApprovalModal(false);
      setShowNotificationsModal(false);
    } catch (error) {
      showToast('Failed to reject time off', 'error');
    }
  };

  const handleEditTimeOffApproval = () => {
    setTimeOffForm({
      employee_id: selectedTimeOffForApproval.employee_id,
      timeOffId: selectedTimeOffForApproval.leave_request_id,
      timeOffTypeId: selectedTimeOffForApproval.time_off_type_id,
      startDate: toLocalISODate(new Date(selectedTimeOffForApproval.start_date)),
      endDate: toLocalISODate(new Date(selectedTimeOffForApproval.end_date)),
      reason: selectedTimeOffForApproval.reason || "",
      startHalfDay: !!selectedTimeOffForApproval.start_half_day,
      endHalfDay: !!selectedTimeOffForApproval.end_half_day,
    });
    setShowTimesheetModal(true);
    setActiveTab("timeOff");
    setShowTimeOffApprovalModal(false);
  };

  const handleEditTimeOffFromNotification = (request) => {
    setTimeOffForm({
      employee_id: request.employee_id,
      timeOffId: request.leave_request_id,
      timeOffTypeId: request.time_off_type_id,
      startDate: toLocalISODate(new Date(request.start_date)),
      endDate: toLocalISODate(new Date(request.end_date)),
      reason: request.reason || "",
      startHalfDay: !!request.start_half_day,
      endHalfDay: !!request.end_half_day,
    });
    setShowTimesheetModal(true);
    setActiveTab("timeOff");
    setShowNotificationsModal(false);
  };

  const fetchPendingTimeOffRequests = useCallback(async () => {
    if (!canApproveTimeOff) return;

    try {
      const response = await getTimeOffRequests({ status: 'pending' });
      const sortedRequests = response.sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
      setPendingTimeOffRequests(sortedRequests);
    } catch (error) {
      console.error('Error fetching pending time off requests:', error);
    }
  }, [canApproveTimeOff]);

  const handleNotificationsClick = () => {
    navigate('/dashboard/notifications?scope=timesheets');
  };

  useEffect(() => {
    if (canApproveTimeOff) {
      fetchPendingTimeOffRequests();
    }
  }, [canApproveTimeOff, fetchPendingTimeOffRequests]);

  const handleNext = () => {
    const next = new Date(currentWeekStart);
    next.setDate(currentWeekStart.getDate() + daysToShow);
    setCurrentWeekStart(getMonday(next));
  };

  const handlePrev = () => {
    const prev = new Date(currentWeekStart);
    prev.setDate(currentWeekStart.getDate() - daysToShow);
    setCurrentWeekStart(getMonday(prev));
  };

  // Show access denied if no view permissions
  if (!canViewAllTimesheets && !canViewOwnTimesheets) {
    return (
      <div className={styles.scheduleWrapper}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h2 className={styles.title}>Timesheets</h2>
          </div>
        </div>
        <div className={styles.accessDenied}>
          <h3>Access Denied</h3>
          <p>You do not have permission to view timesheets.</p>
        </div>
      </div>
    );
  }

  const isViewingOwnTimesheetsOnly = !canViewAllTimesheets && canViewOwnTimesheets;

  // Format date range for display
  const formatDateRange = () => {
    const endDate = new Date(currentWeekStart);
    endDate.setDate(currentWeekStart.getDate() + daysToShow - 1);
    const startMonth = currentWeekStart.toLocaleDateString('en-US', { month: 'short' });
    const endMonth = endDate.toLocaleDateString('en-US', { month: 'short' });
    const startDay = currentWeekStart.getDate();
    const endDay = endDate.getDate();
    const year = endDate.getFullYear();
    
    if (startMonth === endMonth) {
      return `${startMonth} ${startDay} - ${endDay}, ${year}`;
    }
    return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`;
  };

  const formatTimeRange = (startTime, endTime) => {
    if (!startTime || !endTime) return '';
    return `${String(startTime).substring(0, 5)} - ${String(endTime).substring(0, 5)}`;
  };

  const parseColorToRgb = (color) => {
    if (!color || typeof color !== 'string') return null;

    const trimmed = color.trim();

    const hexMatch = trimmed.match(/^#?([a-fA-F0-9]{3}|[a-fA-F0-9]{6})$/);
    if (hexMatch) {
      let hex = hexMatch[1];
      if (hex.length === 3) {
        hex = hex.split('').map((c) => c + c).join('');
      }
      return {
        r: parseInt(hex.substring(0, 2), 16),
        g: parseInt(hex.substring(2, 4), 16),
        b: parseInt(hex.substring(4, 6), 16),
      };
    }

    const rgbMatch = trimmed.match(/^rgba?\(([^)]+)\)$/i);
    if (rgbMatch) {
      const values = rgbMatch[1].split(',').map((v) => Number(v.trim()));
      if (values.length >= 3 && values.slice(0, 3).every((v) => Number.isFinite(v))) {
        return {
          r: Math.max(0, Math.min(255, values[0])),
          g: Math.max(0, Math.min(255, values[1])),
          b: Math.max(0, Math.min(255, values[2])),
        };
      }
    }

    return null;
  };

  const withAlpha = (color, alpha, fallback) => {
    const rgb = parseColorToRgb(color);
    if (!rgb) return fallback;
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
  };

  const renderTimesheetCard = ({
    key,
    companyName,
    clientName,
    projectName,
    hoursText,
    timeRange,
    accentColor,
    variant,
    clickable,
    onClick,
  }) => {
    const isScheduled = variant === 'scheduled';
    const baseAccent = accentColor || '#64748b';

    return (
      <div
      key={key}
      className={`${styles.timesheetBookingBlock} ${variant === 'scheduled' ? styles.timesheetBookingScheduled : styles.timesheetBookingActual}`}
      style={{
        '--timesheet-accent': baseAccent,
        '--timesheet-bg': withAlpha(baseAccent, isScheduled ? 0.13 : 0.18, isScheduled ? '#f8fafc' : '#f3f4f6'),
        '--timesheet-border': withAlpha(baseAccent, isScheduled ? 0.28 : 0.34, '#d1d5db'),
        '--timesheet-border-hover': withAlpha(baseAccent, isScheduled ? 0.45 : 0.52, '#9ca3af'),
        cursor: clickable ? 'pointer' : 'default',
      }}
      onClick={onClick}
    >
      <span className={styles.timesheetBookingAccent} aria-hidden="true" />
      <div className={styles.timesheetBookingBody}>
        <div className={styles.timesheetBookingTitle} title={companyName || 'No Company'}>
          {companyName || 'No Company'}
        </div>
        <div className={styles.timesheetBookingMeta} title={clientName || 'No Client'}>
          {clientName || 'No Client'}
        </div>
        <div className={styles.timesheetBookingProject} title={projectName || 'No Project'}>
          {projectName || 'No Project'}
        </div>
        <div className={styles.timesheetBookingFooter}>
          <span className={styles.timesheetBookingHours}>{hoursText}</span>
          {timeRange && <span className={styles.timesheetBookingRange}>{timeRange}</span>}
        </div>
      </div>
    </div>
    );
  };

  return (
    <div className={styles.scheduleWrapper}>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {showLoading ? (
        <div className={styles.timesheetSkeleton}>
          <div className={styles.timesheetSkeletonHeader}>
            <div className={styles.timesheetSkeletonTitle} />
            <div className={styles.timesheetSkeletonCenter}>
              <div className={styles.timesheetSkeletonIconBtn} />
              <div className={styles.timesheetSkeletonDateBadge} />
              <div className={styles.timesheetSkeletonIconBtn} />
            </div>
            <div className={styles.timesheetSkeletonRight}>
              <div className={styles.timesheetSkeletonToggleGroup}>
                <div className={styles.timesheetSkeletonTogglePill} />
                <div className={styles.timesheetSkeletonTogglePill} />
                <div className={styles.timesheetSkeletonTogglePill} />
              </div>
              <div className={styles.timesheetSkeletonActionBtn} />
              <div className={styles.timesheetSkeletonActionBtn} />
            </div>
          </div>

          <div className={styles.timesheetSkeletonGrid}>
            <div className={styles.timesheetSkeletonTopRow}>
              <div className={styles.timesheetSkeletonNameHeader} />
              {Array.from({ length: 7 }).map((_, idx) => (
                <div key={`timesheet-day-header-${idx}`} className={styles.timesheetSkeletonDayHeader} />
              ))}
            </div>
            {Array.from({ length: 6 }).map((_, rowIdx) => (
              <div key={`timesheet-row-${rowIdx}`} className={styles.timesheetSkeletonRow}>
                <div className={styles.timesheetSkeletonNameCell}>
                  <div className={styles.timesheetSkeletonAvatar} />
                  <div className={styles.timesheetSkeletonNameLine} />
                </div>
                {Array.from({ length: 7 }).map((_, colIdx) => (
                  <div key={`timesheet-cell-${rowIdx}-${colIdx}`} className={styles.timesheetSkeletonDayCell}>
                    {(rowIdx + colIdx) % 3 !== 0 && <div className={styles.timesheetSkeletonChip} />}
                    {(rowIdx + colIdx) % 4 === 0 && <div className={styles.timesheetSkeletonChipShort} />}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className={ui.fadeInContent} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>

      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2 className={styles.title}>
            {isViewingOwnTimesheetsOnly ? 'My Timesheets' : 'Team Timesheets'}
          </h2>
        </div>
        <div className={styles.headerCenter}>
          <button className={styles.navButton} onClick={handlePrev} title="Previous">
            ←
          </button>
          <span className={styles.weekLabel}>{formatDateRange()}</span>
          <button className={styles.navButton} onClick={handleNext} title="Next">
            →
          </button>
        </div>
        <div className={styles.headerRight}>
          <button 
            className={styles.notificationsButton} 
            onClick={handleNotificationsClick}
            title="Notifications"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C10.896 2 10 2.896 10 4V5.586L8.707 6.879C8.077 7.509 7.705 8.358 7.705 9.243V15H6C5.448 15 5 15.448 5 16C5 16.552 5.448 17 6 17H18C18.552 17 19 16.552 19 16C19 15.448 18.552 15 18 15H16.295V9.243C16.295 8.358 15.923 7.509 15.293 6.879L14 5.586V4C14 2.896 13.104 2 12 2Z" fill="currentColor"/>
              <path d="M10 18C10 19.105 10.895 20 12 20C13.105 20 14 19.105 14 18H10Z" fill="currentColor"/>
            </svg>
            {pendingNotificationsCount > 0 && (
              <span className={styles.notificationBadge}>
                {pendingNotificationsCount > 99 ? '99+' : pendingNotificationsCount}
              </span>
            )}
          </button>
          <div className={styles.daySelector}>
            {DAY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                className={`${styles.daySelectorBtn} ${daysToShow === opt.value ? styles.daySelectorActive : ''}`}
                onClick={() => setDaysToShow(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <button 
            className={styles.todayButton} 
            onClick={() => setCurrentWeekStart(getMonday(new Date()))}
          >
            Today
          </button>
          {canViewAllTimesheets && canEditTimesheets && (
            <button
              className={styles.todayButton}
              onClick={async () => {
                setShowVisibilityModal(true);
                await fetchTimesheetVisibilityEmployees();
              }}
              title="Manage employee visibility"
            >
              Employees
            </button>
          )}
          {canApproveTimeOff && pendingTimeOffRequests.length > 0 && (
            <button
              className={styles.todayButton}
              onClick={() => setShowNotificationsModal(true)}
              title="Pending time off requests"
            >
              Pending ({pendingTimeOffRequests.length})
            </button>
          )}
          {hasAnyTimeOffPermission && (
            <button
              className={styles.todayButton}
              onClick={() => {
                setTimeOffForm({
                  timeOffId: null,
                  timeOffTypeId: timeOffTypes.length > 0 ? String(timeOffTypes[0].leave_type_id || timeOffTypes[0].id || '') : "",
                  startDate: toLocalISODate(currentWeekStart),
                  endDate: toLocalISODate(currentWeekStart),
                  reason: "",
                  employee_id: currentUser.user_id,
                });
                setActiveTab("timeOff");
                setShowTimesheetModal(true);
              }}
            >
              Time Off
            </button>
          )}
          <button 
            className={styles.todayButton} 
            onClick={() => setShowExportModal(true)}
            title="Export to CSV"
          >
            Export
          </button>
        </div>
      </div>

      <div className={styles.scheduleScroll}>
        <div 
          className={isViewingOwnTimesheetsOnly ? styles.scheduleGridOwn : styles.scheduleGrid}
          style={{ 
            gridTemplateColumns: isViewingOwnTimesheetsOnly 
              ? `repeat(${daysToShow}, minmax(30px, 1fr))`
              : `200px repeat(${daysToShow}, minmax(30px, 1fr))`
          }}
        >
          {/* Header row - different for own vs team view */}
          {isViewingOwnTimesheetsOnly ? (
            // Simplified header for own timesheets - no employee column
            <div className={styles.headerRow}>
              {visibleDates.map((date, idx) => {
                const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                return (
                  <div 
                    className={`${styles.dayCell} ${styles.timesheetHeaderDayCell} ${isWeekend ? styles.weekendCell : ""}`} 
                    key={idx}
                    style={{ gridColumn: idx + 1 }}
                  >
                    <div className={`${styles.dateHeader} ${styles.timesheetHeaderDateHeader}`}>
                      <div className={styles.dateTop}>
                        {date.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short' })}
                      </div>
                      <div className={styles.dateBottom}>
                        {date.toLocaleDateString('en-ZA', { weekday: 'short' })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            // Standard header with employee column for team view
            <div className={styles.headerRow}>
              <div className={`${styles.nameCell} ${styles.timesheetHeaderNameCell}`}>Employee</div>
              {visibleDates.map((date, idx) => {
                const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                return (
                  <div className={`${styles.dayCell} ${styles.timesheetHeaderDayCell} ${isWeekend ? styles.weekendCell : ""}`} key={idx}>
                    <div className={`${styles.dateHeader} ${styles.timesheetHeaderDateHeader}`}>
                      <div className={styles.dateTop}>
                        {date.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short' })}
                      </div>
                      <div className={styles.dateBottom}>
                        {date.toLocaleDateString('en-ZA', { weekday: 'short' })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Employee rows - different layout for own vs team */}
          {isViewingOwnTimesheetsOnly ? (
            // Single row without employee name column for own timesheets
            <div className={styles.employeeRow}>
              {visibleDates.map((date, idx) => {
                const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                const dateStr = toLocalISODate(date);
                
                const scheduledForDay = scheduledBookings.filter(
                  (b) => {
                    const bStartStr = normalizeDateString(b.start_datetime);
                    const bEndStr = normalizeDateString(b.end_datetime);
                    const overlaps = dateStr >= bStartStr && dateStr <= bEndStr;
                    if (!overlaps) return false;
                    // Check if there is any timesheet for this employee on any date of the booking
                    const hasTimesheetInBooking = timesheets.some(t => {
                      const tDateStr = normalizeDateString(t.work_date);
                      return t.employee_id.toString() === b.employee_id.toString() && tDateStr >= bStartStr && tDateStr <= bEndStr;
                    });
                    return b.employee_id.toString() === currentUser.user_id.toString() && !hasTimesheetInBooking;
                  }
                );

                const timesheetsForDay = timesheets.filter(
                  (t) => {
                    const employeeMatch = t.employee_id.toString() === currentUser.user_id.toString();
                    const timesheetDate = normalizeDateString(t.work_date);
                    return employeeMatch && timesheetDate === dateStr;
                  }
                );

                const timeOffForDay = timeOffRequests.filter(
                  (to) => {
                    const employeeMatch = to.employee_id.toString() === currentUser.user_id.toString();
                    const toStartStr = normalizeDateString(to.start_date);
                    const toEndStr = normalizeDateString(to.end_date);
                    const overlaps = dateStr >= toStartStr && dateStr <= toEndStr;
                    return employeeMatch && overlaps;
                  }
                );

                const hasApprovedTimeOffForDay = timeOffForDay.some((to) => to.status === 'approved');

                return (
                  <div
                    className={`${styles.dayCell} ${isWeekend ? styles.weekendCell : ""}`}
                    key={idx}
                    style={{ 
                      cursor: canCreateTimesheets && !hasApprovedTimeOffForDay ? 'pointer' : 'default',
                      gridColumn: idx + 1 
                    }}
                    onClick={(e) => {
                      if (e.target === e.currentTarget && canCreateTimesheets && !hasApprovedTimeOffForDay) {
                        setTimesheetForm({
                          timesheetId: null,
                          employeeId: currentUser.user_id,
                          workDate: dateStr,
                          projectId: "",
                          projectName: "",
                          projectCompanyName: "",
                          milestoneId: "",
                          activityTypeId: "",
                          startTime: "09:00",
                          endTime: "17:00",
                          description: "",
                        });
                        setShowTimesheetModal(true);
                      }
                    }}
                  >
                    {scheduledForDay.map((b, i) => {
                      const bookingStartTime = new Date(b.start_datetime).toTimeString().substring(0, 5);
                      const bookingEndTime = new Date(b.end_datetime).toTimeString().substring(0, 5);

                      return renderTimesheetCard({
                        key: `scheduled-${i}`,
                        companyName: b.company_name,
                        clientName: b.client_name,
                        projectName: b.project_name,
                        hoursText: `${Number(b.hours_allocated || 0).toFixed(1)}h scheduled`,
                        timeRange: formatTimeRange(bookingStartTime, bookingEndTime),
                        accentColor: b.activity_type_color || b.project_color,
                        variant: 'scheduled',
                        clickable: canCreateTimesheets,
                        onClick: (e) => {
                          e.stopPropagation();
                          if (canCreateTimesheets) {
                            setTimesheetForm({
                              timesheetId: null,
                              employeeId: currentUser.user_id,
                              workDate: dateStr,
                              projectId: b.project_id || "",
                              projectName: b.project_name || "",
                              projectCompanyName: b.company_name || "",
                              milestoneId: b.milestone_id || "",
                              activityTypeId: b.activity_type_id || "",
                              startTime: bookingStartTime,
                              endTime: bookingEndTime,
                              description: b.notes || "",
                            });
                            setPrefillBooking(b);
                            setShowTimesheetModal(true);
                          }
                        },
                      });
                    })}

                    {timesheetsForDay.map((t, i) => {
                      const editable = canCreateTimesheets || canEditTimesheets;

                      return renderTimesheetCard({
                        key: `timesheet-${i}`,
                        companyName: t.company_name,
                        clientName: t.client_name,
                        projectName: t.project_name,
                        hoursText: `${Number(t.hours_worked || 0).toFixed(1)}h actual`,
                        timeRange: formatTimeRange(t.start_time, t.end_time),
                        accentColor: t.activity_type_color || t.project_color,
                        variant: 'actual',
                        clickable: editable,
                        onClick: (e) => {
                          if (editable) {
                            e.stopPropagation();
                            setTimesheetForm({
                              timesheetId: t.timesheet_id,
                              employeeId: currentUser.user_id,
                              workDate: normalizeDateString(t.work_date),
                              projectId: t.project_id,
                              projectName: t.project_name || "",
                              projectCompanyName: t.company_name || "",
                              milestoneId: t.milestone_id || "",
                              activityTypeId: t.activity_type_id || "",
                              startTime: t.start_time?.substring(0, 5) || "09:00",
                              endTime: t.end_time?.substring(0, 5) || "17:00",
                              description: t.description || "",
                            });
                            setShowTimesheetModal(true);
                          }
                        },
                      });
                    })}

                    {timeOffForDay.map((to, i) => (
                      <div
                        key={`timeoff-${i}`}
                        className={styles.timeOffBlock}
                        style={{
                          opacity: to.status === 'pending' ? 0.6 : 1,
                          cursor: 'pointer'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();

                          const isPending = to.status === 'pending';
                          if (isPending && canApproveTimeOff) {
                            setSelectedTimeOffForApproval(to);
                            setShowTimeOffApprovalModal(true);
                            return;
                          }

                          const isOwnRequest = to.employee_id.toString() === currentUser.user_id.toString();
                          const canEditThisTimeOff =
                            (isOwnRequest && canEditOwnPendingTimeOff && isPending) ||
                            canApproveTimeOff ||
                            (canEditApprovedTimeOff && to.status === 'approved');

                          if (!canEditThisTimeOff) {
                            showToast('You do not have permission to edit this time off request', 'error');
                            return;
                          }

                          setTimeOffForm({
                            employee_id: to.employee_id,
                            timeOffId: to.leave_request_id,
                            timeOffTypeId: to.time_off_type_id,
                            startDate: toLocalISODate(new Date(to.start_date)),
                            endDate: toLocalISODate(new Date(to.end_date)),
                            reason: to.reason || "",
                            startHalfDay: !!to.start_half_day,
                            endHalfDay: !!to.end_half_day,
                          });
                          setActiveTab("timeOff");
                          setShowTimesheetModal(true);
                        }}
                      >
                        <div>
                          <div>Time Off {to.status === 'pending' ? '(Pending)' : ''}</div>
                          <div>{to.type_name}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          ) : (
            // Multiple rows with employee names for team view
            employees.map((emp) => {
              const isOwnRow = emp.id.toString() === currentUser.user_id.toString();
              const canClickToCreate = isOwnRow ? canCreateTimesheets : canEditTimesheets;
              const canEditEntry = isOwnRow ? (canCreateTimesheets || canEditTimesheets) : canEditTimesheets;

              return (
                <div className={styles.employeeRow} key={emp.id}>
                  <div className={styles.nameCell}>
                    <div className={styles.employeeAvatar}>
                      {emp.avatar_url ? (
                        <img src={resolveUploadUrl(emp.avatar_url)} alt={emp.name} className={styles.avatarImg} />
                      ) : (
                        <div className={styles.avatarFallback}>
                          {getInitials(emp.name)}
                        </div>
                      )}
                    </div>
                    <div className={styles.employeeInfo}>
                      <div className={styles.employeeName}>{emp.name}</div>
                      <div className={styles.jobTitle}>{emp.job_title}</div>
                    </div>
                  </div>
                  {visibleDates.map((date, idx) => {
                    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                    const dateStr = toLocalISODate(date);
                    
                    const scheduledForDay = scheduledBookings.filter(
                      (b) => {
                        const bStartStr = normalizeDateString(b.start_datetime);
                        const bEndStr = normalizeDateString(b.end_datetime);
                        const overlaps = dateStr >= bStartStr && dateStr <= bEndStr;
                        if (!overlaps) return false;
                        // Check if there is any timesheet for this employee on any date of the booking
                        const hasTimesheetInBooking = timesheets.some(t => {
                          const tDateStr = normalizeDateString(t.work_date);
                          return t.employee_id.toString() === b.employee_id.toString() && tDateStr >= bStartStr && tDateStr <= bEndStr;
                        });
                        return b.employee_id.toString() === emp.id.toString() && !hasTimesheetInBooking;
                      }
                    );

                    const timesheetsForDay = timesheets.filter(
                      (t) => {
                        const employeeMatch = t.employee_id.toString() === emp.id.toString();
                        const timesheetDate = normalizeDateString(t.work_date);
                        return employeeMatch && timesheetDate === dateStr;
                      }
                    );

                    const timeOffForDay = timeOffRequests.filter(
                      (to) => {
                        const employeeMatch = to.employee_id.toString() === emp.id.toString();
                        const toStartStr = normalizeDateString(to.start_date);
                        const toEndStr = normalizeDateString(to.end_date);
                        const overlaps = dateStr >= toStartStr && dateStr <= toEndStr;
                        return employeeMatch && overlaps;
                      }
                    );

                    const hasApprovedTimeOffForDay = timeOffForDay.some((to) => to.status === 'approved');

                    return (
                      <div
                        className={`${styles.dayCell} ${isWeekend ? styles.weekendCell : ""}`}
                        key={idx}
                        style={{ cursor: canClickToCreate && !hasApprovedTimeOffForDay ? 'pointer' : 'default' }}
                        onClick={(e) => {
                          if (e.target === e.currentTarget && canClickToCreate && !hasApprovedTimeOffForDay) {
                            setTimesheetForm({
                              timesheetId: null,
                              employeeId: emp.id,
                              workDate: dateStr,
                              projectId: "",
                              projectName: "",
                              projectCompanyName: "",
                              milestoneId: "",
                              activityTypeId: "",
                              startTime: "09:00",
                              endTime: "17:00",
                              description: "",
                            });
                            setShowTimesheetModal(true);
                          }
                        }}
                      >
                        {scheduledForDay.map((b, i) => {
                          const bookingStartTime = new Date(b.start_datetime).toTimeString().substring(0, 5);
                          const bookingEndTime = new Date(b.end_datetime).toTimeString().substring(0, 5);

                          return renderTimesheetCard({
                            key: `scheduled-${i}`,
                            companyName: b.company_name,
                            clientName: b.client_name,
                            projectName: b.project_name,
                            hoursText: `${Number(b.hours_allocated || 0).toFixed(1)}h scheduled`,
                            timeRange: formatTimeRange(bookingStartTime, bookingEndTime),
                            accentColor: b.activity_type_color || b.project_color,
                            variant: 'scheduled',
                            clickable: canClickToCreate,
                            onClick: (e) => {
                              e.stopPropagation();
                              if (canClickToCreate) {
                                setTimesheetForm({
                                  timesheetId: null,
                                  employeeId: emp.id,
                                  workDate: dateStr,
                                  projectId: b.project_id || "",
                                  projectName: b.project_name || "",
                                  projectCompanyName: b.company_name || "",
                                  milestoneId: b.milestone_id || "",
                                  activityTypeId: b.activity_type_id || "",
                                  startTime: bookingStartTime,
                                  endTime: bookingEndTime,
                                  description: b.notes || "",
                                });
                                setPrefillBooking(b);
                                setShowTimesheetModal(true);
                              }
                            },
                          });
                        })}

                        {timesheetsForDay.map((t, i) => {
                          return renderTimesheetCard({
                            key: `timesheet-${i}`,
                            companyName: t.company_name,
                            clientName: t.client_name,
                            projectName: t.project_name,
                            hoursText: `${Number(t.hours_worked || 0).toFixed(1)}h actual`,
                            timeRange: formatTimeRange(t.start_time, t.end_time),
                            accentColor: t.activity_type_color || t.project_color,
                            variant: 'actual',
                            clickable: canEditEntry,
                            onClick: (e) => {
                              if (canEditEntry) {
                                e.stopPropagation();
                                setTimesheetForm({
                                  timesheetId: t.timesheet_id,
                                  employeeId: emp.id,
                                  workDate: normalizeDateString(t.work_date),
                                  projectId: t.project_id,
                                  projectName: t.project_name || "",
                                  projectCompanyName: t.company_name || "",
                                  milestoneId: t.milestone_id || "",
                                  activityTypeId: t.activity_type_id || "",
                                  startTime: t.start_time?.substring(0, 5) || "09:00",
                                  endTime: t.end_time?.substring(0, 5) || "17:00",
                                  description: t.description || "",
                                });
                                setShowTimesheetModal(true);
                              }
                            },
                          });
                        })}

                        {timeOffForDay.map((to, i) => (
                          <div
                            key={`timeoff-${i}`}
                            className={styles.timeOffBlock}
                            style={{
                              opacity: to.status === 'pending' ? 0.6 : 1,
                              cursor: 'pointer'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();

                              const isPending = to.status === 'pending';
                              if (isPending && canApproveTimeOff) {
                                setSelectedTimeOffForApproval(to);
                                setShowTimeOffApprovalModal(true);
                                return;
                              }

                              const isOwnRequest = to.employee_id.toString() === currentUser.user_id.toString();
                              const canEditThisTimeOff =
                                (isOwnRequest && canEditOwnPendingTimeOff && isPending) ||
                                canApproveTimeOff ||
                                (canEditApprovedTimeOff && to.status === 'approved');

                              if (!canEditThisTimeOff) {
                                showToast('You do not have permission to edit this time off request', 'error');
                                return;
                              }

                              setTimeOffForm({
                                employee_id: to.employee_id,
                                timeOffId: to.leave_request_id,
                                timeOffTypeId: to.time_off_type_id,
                                startDate: toLocalISODate(new Date(to.start_date)),
                                endDate: toLocalISODate(new Date(to.end_date)),
                                reason: to.reason || "",
                                startHalfDay: !!to.start_half_day,
                                endHalfDay: !!to.end_half_day,
                              });
                              setActiveTab("timeOff");
                              setShowTimesheetModal(true);
                            }}
                          >
                            <div>
                              <div>Time Off {to.status === 'pending' ? '(Pending)' : ''}</div>
                              <div>{to.type_name}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
      </div>

        </div>
      )}

      {showTimesheetModal && ((canCreateTimesheets || canEditTimesheets) || hasAnyTimeOffPermission) && (
        <FormModal
          title={activeTab === "timesheet" ? (timesheetForm.timesheetId ? "Edit Timesheet" : "Add Timesheet") : (timeOffForm.timeOffId ? "Edit Time Off Request" : "Request Time Off")}
          onCancel={() => {
            setShowTimesheetModal(false);
            setActiveTab("timesheet");
            setPrefillBooking(null);
            setTimesheetForm({
              timesheetId: null,
              employeeId: "",
              workDate: "",
              projectId: "",
              projectName: "",
              projectCompanyName: "",
              milestoneId: "",
              activityTypeId: "",
              startTime: "09:00",
              endTime: "17:00",
              description: "",
            });
            setTimeOffForm({
              timeOffId: null,
              timeOffTypeId: timeOffTypes.length > 0 ? String(timeOffTypes[0].leave_type_id || timeOffTypes[0].id || '') : "",
              startDate: "",
              endDate: "",
              reason: "",
              employee_id: '',
            });
          }}
          onSubmit={activeTab === "timesheet" ? handleTimesheetSubmit : handleTimeOffSubmit}
          tabs={hasAnyTimeOffPermission ? [
            { key: "timesheet", label: "Timesheet" },
            { key: "timeOff", label: "Time Off" }
          ] : [{ key: "timesheet", label: "Timesheet" }]}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          fields={activeTab === "timesheet" ? [
            // No employee selector for own timesheets view
            (!isViewingOwnTimesheetsOnly && canViewAllTimesheets && canEditTimesheets) && {
              label: "Employee",
              type: "select",
              required: true,
              value: timesheetForm.employeeId,
              onChange: (e) =>
                setTimesheetForm((prev) => ({ 
                  ...prev, 
                  employeeId: e.target.value,
                  projectId: "",
                  projectName: "",
                  projectCompanyName: "",
                  milestoneId: ""
                })),
              options: [
                { value: "", label: "Select employee..." },
                ...(employees || []).map((e) => ({
                  value: e.id,
                  label: e.name + (e.job_title ? ` (${e.job_title})` : ""),
                }))
              ],
            },
            {
              label: "Date",
              type: "date",
              required: true,
              value: timesheetForm.workDate,
              onChange: (e) =>
                setTimesheetForm((prev) => ({ ...prev, workDate: e.target.value })),
            },
            {
              label: "Project/Activity",
              type: "custom",
              required: true,
              render: () => {
                // If editing a timesheet/booking with a project that's no longer in the active list (e.g. completed),
                // include it in the dropdown so the user can still see and manage it
                const bookingProjectInList = timesheetForm.projectId && employeeProjects?.some(
                  p => p.project_id?.toString() === timesheetForm.projectId?.toString()
                );

                const projectList = filteredEmployeeProjects;

                return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', marginBottom: '2px', fontWeight: 500 }}>
                      Company
                    </label>
                    <CustomDropdown
                      options={timesheetCompanyOptions}
                      value={companyFilter}
                      onChange={(val) => {
                        setCompanyFilter(val);
                        setTimesheetForm((prev) => ({
                          ...prev,
                          projectId: "",
                          projectName: "",
                          projectCompanyName: "",
                          milestoneId: "",
                        }));
                      }}
                      disabled={!timesheetForm.employeeId || (employeeProjects.length === 0)}
                      fullWidth
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', marginBottom: '2px', fontWeight: 500 }}>
                        Project
                      </label>
                      <CustomDropdown
                        options={[
                          { value: '', label: timesheetForm.employeeId ? (projectList.length === 0 ? 'No projects available' : 'None') : 'Select employee first' },
                          ...(timesheetForm.projectId && timesheetForm.projectName && !bookingProjectInList
                            ? [{ value: String(timesheetForm.projectId), label: `${timesheetForm.projectName} (${timesheetForm.projectCompanyName || 'No Client'}) — Completed` }]
                            : []),
                          ...projectList.map((project) => ({
                            value: String(project.project_id),
                            label: `${project.project_name} (${project.company_name || 'No Client'})`,
                          })),
                        ]}
                        value={timesheetForm.projectId ? String(timesheetForm.projectId) : ''}
                        onChange={(val) => {
                          const project = employeeProjects.find(
                            (p) => String(p.project_id) === val
                          );
                          setTimesheetForm((prev) => ({
                            ...prev,
                            projectId: val,
                            projectName: project?.project_name || prev.projectName,
                            projectCompanyName: project?.company_name || "",
                            milestoneId: '',
                          }));
                        }}
                        disabled={!timesheetForm.employeeId || (employeeProjects.length === 0 && !timesheetForm.projectName)}
                        fullWidth
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', marginBottom: '2px', fontWeight: 500 }}>
                        Activity Type
                      </label>
                      <CustomDropdown
                        options={[
                          { value: '', label: 'Select activity type...' },
                          ...activityTypes.map((at) => ({
                            value: String(at.activity_type_id),
                            label: at.name,
                          })),
                        ]}
                        value={timesheetForm.activityTypeId ? String(timesheetForm.activityTypeId) : ''}
                        onChange={(val) =>
                          setTimesheetForm((prev) => ({
                            ...prev,
                            activityTypeId: val,
                          }))
                        }
                        fullWidth
                      />
                    </div>
                  </div>
                </div>
              );
              },
            },
            ...(employeeMilestones.length > 0 ? [{
              label: "Milestone",
              type: "select",
              required: false,
              value: timesheetForm.milestoneId,
              onChange: (e) =>
                setTimesheetForm((prev) => ({
                  ...prev,
                  milestoneId: e.target.value,
                })),
              options: [
                { value: "", label: "None" },
                ...employeeMilestones.map((m) => ({
                  value: m.milestone_id,
                  label: `${m.milestone_name}`,
                })),
              ],
            }] : []),

            {
              type: "custom",
              render: () => (
                <DurationScrubber
                  startTime={timesheetForm.startTime}
                  endTime={timesheetForm.endTime}
                  onChange={({ startTime, endTime }) =>
                    setTimesheetForm((prev) => ({ ...prev, startTime, endTime }))
                  }
                />
              )
            },
            {
              label: "Description",
              type: "textarea",
              value: timesheetForm.description,
              onChange: (e) =>
                setTimesheetForm((prev) => ({ ...prev, description: e.target.value })),
            },
          ].filter(Boolean) : [
            {
              label: "Employee",
              type: "select",
              value: timeOffForm.employee_id,
              onChange: (e) =>
                setTimeOffForm((prev) => ({ ...prev, employee_id: e.target.value })),
              options: (canApproveTimeOff || canViewAllTimeOff)
                ? [
                    { value: "", label: "Select employee..." },
                    ...(employees || []).map((e) => ({
                      value: e.id,
                      label: e.name + (e.job_title ? ` (${e.job_title})` : ""),
                    }))
                  ]
                : [{ value: timeOffForm.employee_id, label: currentUser.name + (currentUser.job_title ? ` (${currentUser.job_title})` : "") }],
            },
            {
              label: "Time Off Type",
              type: "select",
              required: true,
              value: timeOffForm.timeOffTypeId,
              disabled: timeOffTypes.length === 0,
              onChange: (e) =>
                setTimeOffForm((prev) => ({ ...prev, timeOffTypeId: e.target.value })),
              options: (() => {
                if (timeOffTypes.length === 0) {
                  return [{ value: "", label: "Loading time off types..." }];
                }
                return timeOffTypes.map(type => ({
                  value: String(type.leave_type_id || type.id || ''),
                  label: type.name || 'Unnamed'
                }));
              })()
            },
            {
              label: "Dates",
              type: "custom",
              render: () => {
                const sameDay = timeOffForm.startDate && timeOffForm.startDate === timeOffForm.endDate;
                const days = countLeaveDays(timeOffForm.startDate, timeOffForm.endDate, timeOffForm.startHalfDay, timeOffForm.endHalfDay);
                return (
                  <div>
                    <div style={{ display: 'flex', gap: '16px' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>Start Date</label>
                        <input
                          type="date"
                          value={timeOffForm.startDate}
                          onChange={(e) =>
                            setTimeOffForm((prev) => ({ ...prev, startDate: e.target.value }))}
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>End Date</label>
                        <input
                          type="date"
                          value={timeOffForm.endDate}
                          onChange={(e) =>
                            setTimeOffForm((prev) => ({ ...prev, endDate: e.target.value }))}
                          style={{ width: '100%' }}
                        />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '16px', marginTop: '8px', fontSize: '0.85rem', color: '#374151' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <input
                          type="checkbox"
                          checked={!!timeOffForm.startHalfDay}
                          onChange={(e) =>
                            setTimeOffForm((prev) => ({ ...prev, startHalfDay: e.target.checked }))}
                        />
                        {sameDay ? 'Half day' : 'First day is a half-day'}
                      </label>
                      {!sameDay && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <input
                            type="checkbox"
                            checked={!!timeOffForm.endHalfDay}
                            onChange={(e) =>
                              setTimeOffForm((prev) => ({ ...prev, endHalfDay: e.target.checked }))}
                          />
                          Last day is a half-day
                        </label>
                      )}
                    </div>
                    {timeOffForm.startDate && timeOffForm.endDate && (
                      <div style={{ marginTop: '6px', fontSize: '0.8rem', color: '#6b7280' }}>
                        {formatDays(days)} working day{days === 1 ? '' : 's'} (weekends excluded)
                      </div>
                    )}
                  </div>
                );
              }
            },
            {
              label: "Reason",
              type: "textarea",
              value: timeOffForm.reason,
              onChange: (e) =>
                setTimeOffForm((prev) => ({ ...prev, reason: e.target.value })),
            },
          ]}
          footerActions={
            activeTab === "timesheet"
              ? (!!timesheetForm.timesheetId && canDeleteTimesheets && (
                  <div style={{ display: "flex", justifyContent: "flex-start" }}>
                    <button
                      type="button"
                      className={styles.buttonDanger}
                      onClick={async () => {
                        const confirmDelete = window.confirm("Are you sure you want to delete this timesheet entry?");
                        if (confirmDelete) {
                          try {
                            await deleteTimesheet(timesheetForm.timesheetId);
                            setShowTimesheetModal(false);
                            setActiveTab("timesheet");
                            await fetchTimesheetsData();
                            showToast('Timesheet deleted successfully', 'success');
                          } catch (err) {
                            console.error("Failed to delete timesheet", err);
                            showToast(err.message || "Failed to delete timesheet", "error");
                          }
                        }
                      }}
                    >
                      Delete Entry
                    </button>
                  </div>
                ))
              : (timeOffForm.timeOffId && (
                  <div style={{ display: "flex", justifyContent: "flex-start" }}>
                    <button
                      type="button"
                      className={styles.buttonDanger}
                      onClick={() => {
                        setTimeOffToCancel(timeOffForm.timeOffId);
                        setShowCancelTimeOffConfirm(true);
                      }}
                    >
                      Cancel Request
                    </button>
                  </div>
                ))
          }
        />
      )}

      {showCancelTimeOffConfirm && (
        <ConfirmDialog
          message="Are you sure you want to cancel this time off request? This action cannot be undone."
          onConfirm={async () => {
            try {
              await cancelTimeOffRequest(timeOffToCancel);
              setShowTimesheetModal(false);
              setActiveTab("timesheet");
              setShowCancelTimeOffConfirm(false);
              setTimeOffToCancel(null);
              await fetchTimeOffRequestsData();
              await fetchPendingTimeOffRequests();
              showToast('Time off request cancelled successfully', 'success');
            } catch (err) {
              console.error("Failed to cancel time off request", err);
              showToast(err.message || "Failed to cancel time off request", "error");
            }
          }}
          onCancel={() => {
            setShowCancelTimeOffConfirm(false);
            setTimeOffToCancel(null);
          }}
          confirmText="Cancel Request"
          cancelText="Cancel"
        />
      )}

      {showTimeOffApprovalModal && selectedTimeOffForApproval && (
        <div className={styles.timeOffApprovalOverlay} onClick={() => setShowTimeOffApprovalModal(false)}>
          <div className={styles.timeOffApprovalDialog} onClick={(e) => e.stopPropagation()}>
            <div className={styles.timeOffApprovalHeader}>
              <h3>Approve Time Off Request</h3>
              <button className={styles.timeOffApprovalCloseBtn} onClick={() => setShowTimeOffApprovalModal(false)}>×</button>
            </div>
            <div className={styles.timeOffApprovalBody}>
              <p><strong>Employee:</strong> {selectedTimeOffForApproval.employee_name}</p>
              <p><strong>Type:</strong> {selectedTimeOffForApproval.type_name}</p>
              <p><strong>Dates:</strong> {new Date(selectedTimeOffForApproval.start_date).toLocaleDateString()} - {new Date(selectedTimeOffForApproval.end_date).toLocaleDateString()}</p>
              <p><strong>Reason:</strong> {selectedTimeOffForApproval.reason || 'No reason provided'}</p>
            </div>
            <div className={styles.timeOffApprovalFooter}>
              <button className={`${styles.timeOffApprovalBtn} ${styles.deny}`} onClick={() => handleDenyTimeOff(selectedTimeOffForApproval.leave_request_id)}>Deny</button>
              <button className={`${styles.timeOffApprovalBtn} ${styles.accept}`} onClick={() => handleApproveTimeOff(selectedTimeOffForApproval.leave_request_id)}>Accept</button>
              <button className={`${styles.timeOffApprovalBtn} ${styles.edit}`} onClick={handleEditTimeOffApproval}>Edit</button>
              <button className={`${styles.timeOffApprovalBtn} ${styles.cancel}`} onClick={() => setShowTimeOffApprovalModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showNotificationsModal && (
        <div className={styles.timeOffApprovalOverlay} onClick={() => setShowNotificationsModal(false)}>
          <div className={styles.timeOffApprovalDialog} onClick={(e) => e.stopPropagation()}>
            <div className={styles.timeOffApprovalHeader}>
              <h3>Pending Time Off Requests</h3>
              <button className={styles.timeOffApprovalCloseBtn} onClick={() => setShowNotificationsModal(false)}>×</button>
            </div>
            <div className={styles.notificationsBody}>
              <ApprovalList
                requests={pendingTimeOffRequests}
                onApprove={handleApproveTimeOff}
                onDeny={handleDenyTimeOff}
                onEdit={handleEditTimeOffFromNotification}
              />
            </div>
          </div>
        </div>
      )}

      {showExportModal && (
        <FormModal
          title="Export Timesheets to CSV"
          onCancel={() => setShowExportModal(false)}
          onSubmit={handleExportSubmit}
          fields={[
            {
              label: "Date Range",
              type: "custom",
              required: true,
              render: () => (
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', marginBottom: '2px', fontWeight: 500 }}>
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={exportForm.startDate}
                      onChange={(e) =>
                        setExportForm((prev) => ({ ...prev, startDate: e.target.value }))
                      }
                      style={{
                        width: '100%',
                        padding: '6px 10px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        fontSize: '11px',
                        background: 'white',
                        color: '#334155',
                      }}
                    />
                  </div>
                  <div style={{ 
                    color: '#94a3b8', 
                    fontSize: '1.2rem',
                    paddingBottom: '6px'
                  }}>→</div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', marginBottom: '2px', fontWeight: 500 }}>
                      End Date
                    </label>
                    <input
                      type="date"
                      value={exportForm.endDate}
                      onChange={(e) =>
                        setExportForm((prev) => ({ ...prev, endDate: e.target.value }))
                      }
                      style={{
                        width: '100%',
                        padding: '6px 10px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        fontSize: '11px',
                        background: 'white',
                        color: '#334155',
                      }}
                    />
                  </div>
                </div>
              ),
            },
            // Employee filter - only show if user can view all timesheets
            canViewAllTimesheets && {
              label: "Employee (optional)",
              type: "select",
              value: exportForm.employeeId,
              onChange: (e) =>
                setExportForm((prev) => ({ ...prev, employeeId: e.target.value })),
              options: [
                { value: "", label: "All employees" },
                ...(employees || []).map((e) => ({
                  value: e.id,
                  label: e.name + (e.job_title ? ` (${e.job_title})` : ""),
                }))
              ],
            },
            {
              label: "Project (optional)",
              type: "select",
              value: exportForm.projectId,
              onChange: (e) =>
                setExportForm((prev) => ({ ...prev, projectId: e.target.value })),
              options: [
                { value: "", label: "All projects" },
                ...Array.from(new Set(timesheets.map(t => t.project_id)))
                  .filter(id => id)
                  .map(projectId => {
                    const timesheet = timesheets.find(t => t.project_id === projectId);
                    return {
                      value: projectId,
                      label: timesheet?.project_name || `Project ${projectId}`
                    };
                  })
              ],
            },
            {
              label: "Client Company (optional)",
              type: "select",
              value: exportForm.clientCompanyId,
              onChange: (e) =>
                setExportForm((prev) => ({ ...prev, clientCompanyId: e.target.value })),
              options: [
                { value: "", label: "All client companies" },
                ...exportClientCompanyOptions,
              ],
            },
            {
              label: "Group By (optional)",
              type: "select",
              value: exportForm.groupBy,
              onChange: (e) =>
                setExportForm((prev) => ({ ...prev, groupBy: e.target.value })),
              options: [
                { value: "", label: "No grouping" },
                { value: "company", label: "Client Company" },
                { value: "contact", label: "Client Name" }
              ],
            },
            {
              type: "custom",
              render: () => (
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={exportForm.includeMilestones}
                    onChange={(e) =>
                      setExportForm((prev) => ({ ...prev, includeMilestones: e.target.checked }))
                    }
                    style={{
                      width: '16px',
                      height: '16px',
                      accentColor: '#3a6aed',
                    }}
                  />
                  <span style={{ fontSize: '11px', color: '#334155' }}>Include milestones in export</span>
                </label>
              ),
            },
            {
              type: "custom",
              render: () => (
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={exportForm.includeActivityTypes}
                    onChange={(e) =>
                      setExportForm((prev) => ({ ...prev, includeActivityTypes: e.target.checked }))
                    }
                    style={{
                      width: '16px',
                      height: '16px',
                      accentColor: '#3a6aed',
                    }}
                  />
                  <span style={{ fontSize: '11px', color: '#334155' }}>Include activity types in export</span>
                </label>
              ),
            },
          ].filter(Boolean)}
        />
      )}

      {showVisibilityModal && (
        <FormModal
          title="Employee Visibility (Timesheets)"
          onCancel={() => setShowVisibilityModal(false)}
          onSubmit={(e) => {
            e.preventDefault();
            setShowVisibilityModal(false);
          }}
          fields={[
            {
              type: "custom",
              render: () => (
                <div style={{ maxHeight: '360px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {isVisibilityLoading ? (
                    <div style={{ color: '#64748b', fontSize: '0.9rem' }}>Loading employees...</div>
                  ) : visibilityEmployees.length === 0 ? (
                    <div style={{ color: '#64748b', fontSize: '0.9rem' }}>No employees found.</div>
                  ) : (
                    visibilityEmployees.map((employee) => (
                      <label
                        key={employee.employee_id}
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                      >
                        <div>
                          <div style={{ fontSize: '0.9rem', fontWeight: 500, color: '#1e293b' }}>{employee.name}</div>
                          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{employee.job_title || 'No title'}</div>
                        </div>
                        <input
                          type="checkbox"
                          checked={!employee.hide_on_timesheets}
                          onChange={(e) => handleTimesheetVisibilityToggle(employee.employee_id, !e.target.checked)}
                        />
                      </label>
                    ))
                  )}
                </div>
              ),
            },
          ]}
        />
      )}
    </div>
  );
};

export default Timesheets;